import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { layout } from '../../constants/layout';
import { routes, type AuthStackParamList } from '../../constants/routes';
import type { ApiError } from '../../api/apiError';
import { getAppInfo } from '../../config/appInfo';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';

const loginMessages: Record<string, string> = {
  INVALID_CREDENTIALS: 'Invalid username or password.',
  LOGIN_NOT_ALLOWED: 'Unable to complete login.',
  NETWORK_ERROR: 'Unable to connect to the server.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
  AUTH_RESPONSE_INVALID:
    'Sign-in succeeded but the session could not be saved. Try again.',
};

function safeLoginMessage(error: unknown): string {
  const apiError = error as ApiError;
  if (apiError?.code && loginMessages[apiError.code]) {
    return loginMessages[apiError.code];
  }
  if (typeof apiError?.message === 'string' && apiError.message.trim()) {
    if (apiError.code || apiError.statusCode !== undefined) {
      return apiError.message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return apiError?.statusCode === undefined
    ? 'Unable to connect to the server.'
    : 'Unable to sign in. Please try again.';
}

export function LoginScreen() {
  const auth = useAuth();
  const palette = useAppColors();
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList>>();
  const canGoBack = navigation.canGoBack();
  const appName = useMemo(() => getAppInfo().appName, []);
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);


  const themed = useMemo(
    () => ({
      brand: {
        ...typography.screenTitle,
        color: palette.white,
        fontWeight: '700' as const,
      },
      brandTag: {
        ...typography.caption,
        color: palette.white,
        opacity: 0.85,
        textTransform: 'uppercase' as const,
        letterSpacing: 1.2,
        fontWeight: '700' as const,
      },
      title: { ...typography.sectionTitle, color: palette.textPrimary },
      subtitle: { ...typography.body, color: palette.textSecondary },
      forgot: {
        ...typography.body,
        color: palette.primary,
        textAlign: 'center' as const,
        fontWeight: '600' as const,
      },
      error: {
        ...typography.caption,
        color: palette.error,
        textAlign: 'center' as const,
      },
    }),
    [palette],
  );

  function handleForgotPassword() {
    setSubmitError('');
    auth.clearSessionMessage();
    navigation.navigate(routes.forgotPassword, {
      loginName: loginName.trim() || undefined,
    });
  }

  async function handleLogin() {
    if (submitting) return;
    const normalizedLoginName = loginName.trim();
    const nextLoginError = normalizedLoginName ? '' : 'Login name is required.';
    const nextPasswordError = password ? '' : 'Password is required.';
    setLoginError(nextLoginError);
    setPasswordError(nextPasswordError);
    setSubmitError('');
    auth.clearSessionMessage();
    if (nextLoginError || nextPasswordError) return;

    setSubmitting(true);
    try {
      await auth.signIn(normalizedLoginName, password);
      setPassword('');
    } catch (error) {
      setPassword('');
      setSubmitError(safeLoginMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppScreen
      scroll
      keyboardSafe
      webLayout="fluid"
      contentStyle={styles.screen}
      backgroundColor={palette.background}
    >
      <View style={[styles.hero, { backgroundColor: palette.brand }]}>
        {canGoBack ? (
          <Pressable
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Back"
            hitSlop={10}
            style={({ pressed }) => [
              styles.backButton,
              {
                backgroundColor: pressed
                  ? 'rgba(255,255,255,0.22)'
                  : 'rgba(255,255,255,0.14)',
              },
            ]}
          >
            <Text style={styles.backChevron}>‹</Text>
          </Pressable>
        ) : null}
        <Text style={themed.brandTag}>{appName}</Text>
        <Text style={themed.brand}>{appName}</Text>
      </View>

      <View
        style={[
          styles.formArea,
          { maxWidth: layout.webFormMaxWidth, width: '100%', alignSelf: 'center' },
        ]}
      >
        <Text style={themed.title}>Welcome Back</Text>
        <Text style={themed.subtitle}>Sign in to continue to your workspace.</Text>

        <AppCard style={styles.card}>
          <AppInput
            label="Email or username"
            value={loginName}
            onChangeText={value => {
              setLoginName(value);
              if (submitError) setSubmitError('');
            }}
            placeholder="Enter your email or username"
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="username"
            error={loginError}
          />
          <AppInput
            label="Password"
            value={password}
            onChangeText={value => {
              setPassword(value);
              if (submitError) setSubmitError('');
            }}
            placeholder="Enter your password"
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="password"
            error={passwordError}
          />
          <AppButton title="Login" onPress={handleLogin} loading={submitting} />
          <Pressable
            onPress={handleForgotPassword}
            accessibilityRole="button"
            accessibilityLabel="Forgot Password"
            hitSlop={8}
            style={({ pressed }) => [
              styles.forgotButton,
              { opacity: pressed ? 0.7 : 1 },
            ]}
          >
            <Text style={themed.forgot}>Forgot Password?</Text>
          </Pressable>
          {auth.sessionMessage || submitError ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {auth.sessionMessage || submitError}
            </Text>
          ) : null}
        </AppCard>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  screen: { paddingHorizontal: 0, paddingVertical: 0 },
  hero: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xxl,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
    gap: spacing.sm,
  },
  backButton: {
    alignSelf: 'flex-start',
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  backChevron: {
    color: '#FFFFFF',
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '300',
    marginTop: -2,
  },
  formArea: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    gap: spacing.sm,
  },
  card: { gap: spacing.lg, marginTop: spacing.md, maxWidth: '100%', minWidth: 0 },
  forgotButton: {
    alignSelf: 'center',
    paddingVertical: spacing.xs,
  },
});
